"use client"

import type { Locale } from "next-intl"
import { Icon } from "@iconify/react"
import clsx from "clsx"
import { useTransition } from "react"
import { getQueryClient } from "@/app/[locale]/get-query-client"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { usePathname, useRouter } from "@/i18n/navigation"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar"

type Language = {
  label: string
  value: Locale
  icon: string
}

type Props = {
  currentLocale: Locale
  languages: Language[]
}

export function LanguageSelector({ currentLocale, languages }: Props) {
  const router = useRouter()
  const { isMobile } = useSidebar()
  const pathname = usePathname()
  const [, startTransition] = useTransition()

  const handleChange = (lang: Locale) => {
    const queryClient = getQueryClient() // 👈 Reutiliza el cliente existente (SSR/CSR)
    queryClient.clear() // Limpia la caché para forzar el refetch en nuevo idioma

    startTransition(() => {
      router.replace(pathname, { locale: lang })
    })
  }

  const current = languages.find(lang => lang.value === currentLocale)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Icon icon={current?.icon ?? "lucide:globe"} className="h-5 w-5" />
              <span>{current?.label ?? currentLocale}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            {languages.map(lang => (
              <DropdownMenuItem
                key={lang.value}
                disabled={lang.value === currentLocale}
                onClick={() => handleChange(lang.value)}
                className={clsx(
                  "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors duration-150",
                  lang.value === currentLocale
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-accent",
                )}
              >
                <Icon icon={lang.icon} className="h-5 w-5" />
                <span>{lang.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
